import React from 'react';
import { Animated, Easing, StyleSheet, View } from 'react-native';

import { BigButton } from '@components/Button';
import type { AnyFieldLikeMetaBase, DeepKeys, Updater } from '@tanstack/form-core';

import type { FormSubmitButtonProps } from './SubmitButton';

export type MultiStepFormWizardForm<TFormValues extends object> = {
  handleSubmit: () => Promise<void>;
  state: {
    isValid: boolean;
    values: TFormValues;
  };
  setFieldMeta: <TField extends DeepKeys<TFormValues>>(
    field: TField,
    updater: Updater<AnyFieldLikeMetaBase>
  ) => void;
  SubmitBigButton: React.ComponentType<FormSubmitButtonProps>;
  Subscribe: <TSelected>(props: {
    selector: (state: { values: TFormValues }) => TSelected;
    children: (selected: TSelected) => React.ReactNode;
  }) => React.ReactNode | Promise<React.ReactNode>;
  validateField: <TField extends DeepKeys<TFormValues>>(
    field: TField,
    cause: 'submit'
  ) => unknown[] | Promise<unknown[]>;
};

export type MultiStepFormWizardStep<
  TFormValues extends object,
  TForm extends MultiStepFormWizardForm<TFormValues>,
> = {
  /** Stable identifier used for rendering and transition bookkeeping. */
  id: string;
  /** Maps local step field names to parent form field paths for grouped validation. */
  fields: Record<string, DeepKeys<TFormValues>>;
  /** Overrides which local fields from `fields` are validated before advancing. */
  validateFields?: readonly string[];
  /** Hides the step when it does not apply to the current form values. */
  isVisible?: (values: TFormValues) => boolean;
  /** Label for the primary button while this step is active. */
  nextLabel?: string;
  /** Test id for the primary button while this step is active. */
  primaryButtonTestID?: string;
  /** Renders the step body. The step is only mounted while active. */
  render: (props: { form: TForm; values: TFormValues }) => React.ReactNode;
};

export type MultiStepFormWizardProps<
  TFormValues extends object,
  TForm extends MultiStepFormWizardForm<TFormValues>,
> = {
  /** Form instance that owns values, validation, and final submit validation. */
  form: TForm;
  /** Ordered steps in the wizard. Invisible steps are skipped. */
  steps: readonly MultiStepFormWizardStep<TFormValues, TForm>[];
  /** Label for the final submit button. */
  submitLabel: string;
  /** Test id for the final submit button. */
  submitButtonTestID: string;
  /** Base test id for the wizard shell. */
  testID?: string;
};

const STEP_TRANSITION_MS = 250;

export function MultiStepFormWizard<
  TFormValues extends object,
  TForm extends MultiStepFormWizardForm<TFormValues>,
>({
  form,
  steps,
  submitLabel,
  submitButtonTestID,
  testID = 'components.Form.MultiStepFormWizard',
}: MultiStepFormWizardProps<TFormValues, TForm>) {
  return (
    <form.Subscribe selector={(state) => state.values}>
      {(values) => (
        <MultiStepFormWizardContent
          form={form}
          steps={steps}
          values={values}
          submitLabel={submitLabel}
          submitButtonTestID={submitButtonTestID}
          testID={testID}
        />
      )}
    </form.Subscribe>
  );
}

type MultiStepFormWizardContentProps<
  TFormValues extends object,
  TForm extends MultiStepFormWizardForm<TFormValues>,
> = Omit<MultiStepFormWizardProps<TFormValues, TForm>, 'steps'> & {
  steps: readonly MultiStepFormWizardStep<TFormValues, TForm>[];
  values: TFormValues;
};

function MultiStepFormWizardContent<
  TFormValues extends object,
  TForm extends MultiStepFormWizardForm<TFormValues>,
>({
  form,
  steps,
  values,
  submitLabel,
  submitButtonTestID,
  testID = 'components.Form.MultiStepFormWizard',
}: MultiStepFormWizardContentProps<TFormValues, TForm>) {
  const [activeStepId, setActiveStepId] = React.useState(steps[0]?.id);
  const [transitionDirection, setTransitionDirection] = React.useState(1);
  const [transition] = React.useState(() => new Animated.Value(1));

  const visibleSteps = React.useMemo(
    () => steps.filter((step) => !step.isVisible || step.isVisible(values)),
    [steps, values]
  );

  const resolvedActiveStepId = visibleSteps.some((step) => step.id === activeStepId)
    ? activeStepId
    : visibleSteps[0]?.id;

  const activeStepIndex = Math.max(
    0,
    visibleSteps.findIndex((step) => step.id === resolvedActiveStepId)
  );
  const activeStep = visibleSteps[activeStepIndex];
  const isLastStep = activeStepIndex === visibleSteps.length - 1;

  React.useEffect(() => {
    transition.setValue(0);
    Animated.timing(transition, {
      duration: STEP_TRANSITION_MS,
      easing: Easing.out(Easing.cubic),
      toValue: 1,
      useNativeDriver: true,
    }).start();
  }, [activeStep?.id, transition]);

  const animatedStyle = {
    opacity: transition,
    transform: [
      {
        translateX: transition.interpolate({
          inputRange: [0, 1],
          outputRange: [24 * transitionDirection, 0],
        }),
      },
    ],
  };

  const goToStep = (nextIndex: number) => {
    const nextStep = visibleSteps[nextIndex];
    if (!nextStep) {
      return;
    }

    setTransitionDirection(nextIndex > activeStepIndex ? 1 : -1);
    setActiveStepId(nextStep.id);
  };

  const onNext = async () => {
    if (!activeStep) {
      return;
    }

    if (!(await validateStep(form, activeStep))) {
      return;
    }

    goToStep(activeStepIndex + 1);
  };

  if (!activeStep) {
    return null;
  }

  return (
    <View testID={testID}>
      <Animated.View style={animatedStyle} testID={`${testID}.Step.${activeStep.id}`}>
        {activeStep.render({ form, values })}
      </Animated.View>
      <View style={styles.actions}>
        {activeStepIndex > 0 ? (
          <BigButton
            icon="arrow-left"
            mode="outlined"
            onPress={() => goToStep(activeStepIndex - 1)}
            style={styles.actionButton}
            testID={`${testID}.Button.Back`}>
            Back
          </BigButton>
        ) : null}
        {isLastStep ? (
          <form.SubmitBigButton
            mode="contained"
            style={styles.actionButton}
            testID={submitButtonTestID}>
            {submitLabel}
          </form.SubmitBigButton>
        ) : (
          <BigButton
            contentStyle={styles.forwardButtonContent}
            icon="arrow-right"
            mode="contained"
            onPress={onNext}
            style={styles.actionButton}
            testID={activeStep.primaryButtonTestID}>
            {activeStep.nextLabel ?? 'Next'}
          </BigButton>
        )}
      </View>
    </View>
  );
}

async function validateStep<
  TFormValues extends object,
  TForm extends MultiStepFormWizardForm<TFormValues>,
>(form: TForm, step: MultiStepFormWizardStep<TFormValues, TForm>) {
  const fieldNames = step.validateFields ?? Object.keys(step.fields);
  const fieldErrors = await Promise.all(
    fieldNames.map(async (fieldName) => {
      const fieldPath = step.fields[fieldName];
      const errors = await Promise.resolve(form.validateField(fieldPath, 'submit'));
      return { errors, fieldPath };
    })
  );

  fieldErrors.forEach(({ errors, fieldPath }) => {
    if (errors.length === 0) {
      return;
    }

    form.setFieldMeta(fieldPath, (previous) => ({
      ...previous,
      isBlurred: true,
      isTouched: true,
    }));
  });

  return fieldErrors.every(({ errors }) => errors.length === 0);
}

const styles = StyleSheet.create({
  actionButton: {
    flex: 1,
    marginTop: 15,
    width: undefined,
  },
  actions: {
    flexDirection: 'row',
    gap: 10,
    marginHorizontal: 20,
  },
  forwardButtonContent: {
    flexDirection: 'row-reverse',
  },
});
